import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementBlock as _createElementBlock, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "ml-auto" }
const _hoisted_2 = ["textContent"]
const _hoisted_3 = ["textContent"]

import { computed, onMounted, Ref, ref } from 'vue';
import Column from 'primevue/column';
import Chip from 'primevue/chip';
import DataTable from 'primevue/datatable';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useArchiveStore, useFacilitatorStore } from '@/store';
import { FacilitatorTableInterface } from '@/types/Facilitator';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';


export default /*@__PURE__*/_defineComponent({
  __name: 'FacilitatorsTable',
  setup(__props) {

onMounted(() => {
  if (can([PERMISSIONS.ORGANIZATION_FACILITATORS_INDEX])) {
    return getFacilitators(false);
  }
});

const facilitatorStore = useFacilitatorStore();
const archiveStore = useArchiveStore();
const facilitators: Ref<FacilitatorTableInterface[]> = ref([]);
const facilitatorCount: Ref<number> = computed(() => (facilitators.value ? facilitators.value.length : 0));
const facilitatorModalOpen = ref(false);
const archivedVisible = ref(false);
const facilitatorsItem: Ref<{ name: string; value: number }[]> = ref([]);

const getFacilitators: (showArchived: boolean) => Promise<void> = async (showArchived) => {
  const filterArchived = showArchived ? '?archived=1' : '';
  const facilitatorData = await facilitatorStore.getFacilitatorsForTable(filterArchived).catch((e) => {
    facilitators.value = [];
    throw e;
  });
  const filteredFacilitators = facilitatorData.filter(
    (facilitator) => facilitator.organizations && facilitator.organizations[0].id === facilitatorStore.orgId,
  );
  const parentOrgFacilitators = facilitatorData.filter(
    (facilitator) => facilitator.organizations && facilitator.organizations[0].id != facilitatorStore.orgId,
  );
  facilitators.value = [
    ...filteredFacilitators.sort((a, b) => a.name.localeCompare(b.name)),
    ...parentOrgFacilitators.sort((a, b) => a.name.localeCompare(b.name)),
  ];
};

const createMenuModel = (item: FacilitatorTableInterface) => {
  const menuItems = [
    {
      label: 'Remove Facilitator',
      visible: can([PERMISSIONS.ORGANIZATION_DETACH_FACILITATORS]),
      command: () => {
        detachFacilitators([item.id], item.name);
      },
    },
  ];
  if (item.archived) {
    menuItems.push({
      label: 'Unarchive',
      visible: can([PERMISSIONS.PEOPLE_UNARCHIVE]),
      command: async () => {
        await archiveStore.unarchive(item.id);
        archiveStore.doToast(`${item.name} has been restored`, 'Unarchived');
      },
    });
  }
  return menuItems;
};

const attachFacilitators: (ids: number[]) => Promise<void> = async (ids) => {
  await facilitatorStore.attachFacilitators({ facilitators: ids });
  await getFacilitators(false);
  facilitatorStore.doToast('Facilitator(s) has been added', 'Added');
};

const detachFacilitators: (ids: number[], facilitatorName: string) => Promise<void> = async (ids, facilitatorName) => {
  await facilitatorStore.detachFacilitators({ facilitators: ids });
  await getFacilitators(false);
  facilitatorStore.doToast(`${facilitatorName} has been removed from organization`, 'Removed');
};

const openAddFacilitators = async () => {
  facilitatorModalOpen.value = true;
  facilitatorsItem.value = await facilitatorStore.getAddFacilitatorsModalData();
};

const onCancelAdd = () => (facilitatorModalOpen.value = false);

const onSaveAdd = (facilitators: { name: string; value: number }[]) => {
  let facilitatorIds = facilitators.map((facilitator) => {
    return facilitator.value;
  });
  attachFacilitators(facilitatorIds);
  facilitatorModalOpen.value = false;
};

const showArchived = () => {
  archivedVisible.value = true;
  facilitatorStore.showArchived = true;
  getFacilitators(true);
};

const hideArchived = () => {
  archivedVisible.value = false;
  facilitatorStore.showArchived = false;
  getFacilitators(false);
};

const setRowClass = (rowData: FacilitatorTableInterface) => {
  return rowData.organizations && rowData.organizations[0].id != facilitatorStore.orgId
    ? 'inherited-service-provider-row'
    : 'service-provider-row';
};

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_ActionMenu = _resolveComponent("ActionMenu")!
  const _component_Card = _resolveComponent("Card")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_component_Card, null, {
    header: _withCtx(() => [
      _cache[0] || (_cache[0] = _createElementVNode("h2", null, "Facilitators", -1)),
      _createVNode(_unref(Chip), null, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(facilitatorCount.value), 1)
        ]),
        _: 1
      }),
      (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_FACILITATORS_SHOW_ARCHIVED]))
        ? (_openBlock(), _createBlock(_unref(ArchiveSwitch), {
            key: 0,
            onEnable: showArchived,
            onDisable: hideArchived
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_1, [
        (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_ATTACH_FACILITATORS]))
          ? (_openBlock(), _createBlock(_component_AppButton, {
              key: 0,
              id: "add-button",
              type: "text",
              size: "small",
              text: "Add Facilitators",
              "icon-left": "add",
              onClick: openAddFacilitators
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    content: _withCtx(() => [
      _createVNode(_unref(DataTable), {
        value: facilitators.value,
        "global-filter-fields": ['name'],
        scrollable: true,
        "loading-icon": "logo-spinner",
        loading: _unref(facilitatorStore).loading,
        "data-key": "name",
        "responsive-layout": "scroll",
        "row-class": setRowClass,
        "scroll-height": "440px",
        "state-key": "session-store-facilitators-table",
        "state-storage": "session",
        class: _normalizeClass({ 'table-loading': _unref(facilitatorStore).loading })
      }, {
        empty: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode("Facilitators not assigned", -1)
        ]))]),
        default: _withCtx(() => [
          (facilitatorCount.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_unref(Column), {
                  "body-style": "max-width: 20rem",
                  field: "name",
                  header: "Facilitator Name",
                  "header-style": "max-width: 20rem; color: #3B4242;",
                  sortable: ""
                }, {
                  body: _withCtx(({ data }) => [
                    (data.archived)
                      ? _withDirectives((_openBlock(), _createBlock(_component_Icon, {
                          key: 0,
                          name: "info",
                          class: "mr-1 text-color-gray-dark text-base cursor-pointer"
                        }, null, 512)), [
                          [
                            _directive_tooltip,
                            {
                  value: 'Archived',
                  escape: true,
                  fitContent: true,
                },
                            void 0,
                            { top: true }
                          ]
                        ])
                      : _createCommentVNode("", true),
                    (_unref(can)([_unref(PERMISSIONS).PEOPLE_SHOW]))
                      ? (_openBlock(), _createBlock(_component_RouterLink, {
                          key: 1,
                          to: `/user/${data.id}`,
                          class: "font-semibold",
                          textContent: _toDisplayString(data.name)
                        }, null, 8, ["to", "textContent"]))
                      : (_openBlock(), _createElementBlock("span", {
                          key: 2,
                          class: "font-semibold",
                          textContent: _toDisplayString(data.name)
                        }, null, 8, _hoisted_2))
                  ]),
                  _: 1
                }),
                _createVNode(_unref(Column), {
                  field: "organizations",
                  header: "Organization"
                }, {
                  body: _withCtx(({ data }) => [
                    (data.organizations[0].id != _unref(facilitatorStore).orgId)
                      ? (_openBlock(), _createBlock(_component_RouterLink, {
                          key: 0,
                          class: "text-color-secondary underline",
                          to: `/organizations/${data.organizations[0].id}`,
                          textContent: _toDisplayString(data.organizations[0].name)
                        }, null, 8, ["to", "textContent"]))
                      : (_openBlock(), _createElementBlock("span", {
                          key: 1,
                          textContent: _toDisplayString(data.organizations[0].name)
                        }, null, 8, _hoisted_3))
                  ]),
                  _: 1
                }),
                (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_DETACH_FACILITATORS, _unref(PERMISSIONS).PEOPLE_UNARCHIVE]))
                  ? (_openBlock(), _createBlock(_unref(Column), {
                      key: 0,
                      "body-style": "justify-content: center; font-size: .8725rem; max-width: 10rem;",
                      "header-style": "justify-content: center; max-width: 10rem",
                      field: "id",
                      header: "Actions"
                    }, {
                      body: _withCtx(({ data }) => [
                        (data.organizations[0].id == _unref(facilitatorStore).orgId)
                          ? (_openBlock(), _createBlock(_component_ActionMenu, {
                              key: 0,
                              "menu-items": createMenuModel(data),
                              icon: "more_horiz"
                            }, null, 8, ["menu-items"]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["value", "loading", "class"]),
      (facilitatorModalOpen.value && _unref(can)([_unref(PERMISSIONS).ORGANIZATION_ATTACH_FACILITATORS]))
        ? (_openBlock(), _createBlock(AddPersonModal, {
            key: 0,
            title: "Add facilitators",
            items: facilitatorsItem.value,
            searchable: true,
            "list-header-text": "Facilitator name",
            "button-text": "Add facilitators",
            placeholder: "Search facilitators",
            "person-type": "facilitator",
            onAddItems: onSaveAdd,
            onCancel: onCancelAdd
          }, null, 8, ["items"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})